import { useEffect, useState } from "react";
import axios from "axios";
import { useOperator } from "@/lib/operator";
import { Loader2, Delete, ArrowLeft, AlertTriangle } from "lucide-react";
import AddToHomeScreenPrompt from "@/components/AddToHomeScreenPrompt";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OperatorLock() {
  const { login } = useOperator();
  const [operators, setOperators] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pin, setPin] = useState("");
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/app/operators`)
      .then(r => setOperators(r.data || []))
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  const submit = async (fullPin) => {
    if (!selected || fullPin.length < 4) return;
    setBusy(true); setErr(null);
    try {
      await login(selected.id, fullPin);
    } catch (e) {
      setErr(e.response?.data?.detail || "Wrong PIN");
      setPin("");
    } finally { setBusy(false); }
  };

  const press = (k) => {
    if (busy) return;
    setErr(null);
    if (k === "del")   return setPin(p => p.slice(0, -1));
    if (k === "clear") return setPin("");
    if (pin.length >= 6) return;
    const next = pin + k;
    setPin(next);
    if (next.length === 4) submit(next);
  };

  if (loading) {
    return <div className="h-screen grid place-items-center text-slate-400 bg-slate-50"><Loader2 className="animate-spin" /></div>;
  }

  // Step 1: pick name
  if (!selected) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        {/* Brand header */}
        <header className="bg-white border-b-4 border-[#c41e3a] px-4 py-3 flex items-center justify-center shadow-sm">
          <img src="/wilson-logo.png" alt="Wilson Contractors" className="h-12 w-auto" />
        </header>

        <main className="flex-1 flex flex-col items-center justify-center px-6 py-10">
          <div className="mb-10 text-center">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#c41e3a] font-bold mb-1">Wilson Contractors · Field App</div>
            <h1 className="font-display font-black text-4xl text-[#1a1a5c]">Who's working today?</h1>
            <p className="text-slate-500 text-sm mt-2">Tap your name to sign in</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4 w-full max-w-3xl">
            {operators.map(o => (
              <button key={o.id} onClick={() => setSelected(o)} data-testid={`op-pick-${o.name}`}
                      className="bg-white hover:bg-[#1a1a5c] border-2 border-slate-200 hover:border-[#1a1a5c] rounded-2xl py-8 px-4 transition-all active:scale-95 group shadow-sm hover:shadow-xl">
                <div className="w-20 h-20 mx-auto rounded-full bg-[#1a1a5c] group-hover:bg-[#c41e3a] grid place-items-center text-2xl font-black text-white mb-3 transition-colors">
                  {o.initials}
                </div>
                <div className="text-[#1a1a5c] group-hover:text-white font-display font-bold text-xl transition-colors">{o.name}</div>
              </button>
            ))}
          </div>
          {err && (
            <div className="mt-6 text-[#c41e3a] text-sm flex items-center gap-2">
              <AlertTriangle size={14} /> {err}
            </div>
          )}
        </main>
      </div>
    );
  }

  // Step 2: PIN entry
  const keys = ["1","2","3","4","5","6","7","8","9","clear","0","del"];
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b-4 border-[#c41e3a] px-4 py-3 flex items-center justify-between shadow-sm">
        <button onClick={() => { setSelected(null); setPin(""); setErr(null); }} data-testid="op-back"
                className="text-slate-600 hover:text-[#c41e3a] flex items-center gap-1 text-sm font-bold">
          <ArrowLeft size={16} /> Back
        </button>
        <img src="/wilson-logo.png" alt="Wilson Contractors" className="h-10 w-auto" />
        <div className="w-12" />
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-6 py-10">
        <div className="text-center mb-6">
          <div className="w-24 h-24 mx-auto rounded-full bg-[#1a1a5c] grid place-items-center text-3xl font-black text-white mb-4 shadow-lg">
            {selected.initials}
          </div>
          <div className="text-[#1a1a5c] font-display font-bold text-2xl">Hi {selected.name}</div>
          <p className="text-slate-500 text-sm mt-1">Enter your 4-digit PIN</p>
        </div>

        <div className="flex gap-3 mb-7" data-testid="op-pin-dots">
          {[0,1,2,3].map(i => (
            <div key={i} className={`w-4 h-4 rounded-full transition-all ${i < pin.length ? "bg-[#c41e3a] scale-110" : "bg-slate-300"}`} />
          ))}
        </div>

        <div className="grid grid-cols-3 gap-3 w-full max-w-xs">
          {keys.map(k => (
            <button key={k} onClick={() => press(k)} disabled={busy} data-testid={`pin-${k}`}
                    className={`h-16 rounded-xl font-display font-bold text-2xl transition-all active:scale-90 shadow-sm ${
                      k === "clear" ? "bg-white text-slate-500 text-sm border border-slate-200" :
                      k === "del"   ? "bg-white text-slate-600 border border-slate-200" :
                                      "bg-white hover:bg-[#1a1a5c] hover:text-white text-[#1a1a5c] border border-slate-200"
                    }`}>
              {k === "del" ? <Delete className="mx-auto" size={20} /> : k === "clear" ? "CLEAR" : k}
            </button>
          ))}
        </div>

        {err && <div className="mt-6 text-[#c41e3a] text-sm flex items-center gap-2 font-bold"><AlertTriangle size={14} /> {err}</div>}
        {busy && <div className="mt-6 text-[#1a1a5c] text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Signing in…</div>}
      </main>
      <AddToHomeScreenPrompt />
    </div>
  );
}
